/* eslint-disable no-nested-ternary */
import currency from "currency.js";
import { useContext, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSWRConfig } from "swr";
import ConfirmationModal from ".";
import { InstrumentTypeResponse, SideResponse } from "../../codegen-api";
import {
  BACKGROUND_COLORS,
  COLORS,
} from "../../constants/design/colors";
import { useGetAccount } from "../../hooks/api/account/useGetAccount";
import { useOrder } from "../../hooks/api/order/useOrder";
import { usePerpOrder } from "../../hooks/api/order/usePerpOrder";
import { useToast } from "../../hooks/toast";
import { useSFX } from "../../hooks/useSFX";
import useOrderbookWSS from "../../hooks/wss/useOrderbookWSS";
import { ClosePositionOverlayInfo } from "../shared/ClosePositionOverlayInfo";
import { PnLCardModal } from "../PnLCardModal";
import { MarketInstrumentContext } from "../../contexts/MarketInstrumentContext";
import { useRFQ } from "../../hooks/api/rfq/useRFQ";
import { useQuotes } from "../../hooks/api/rfq/useQuotes";
import {
  ClosePositionPageEnum,
  IClosePositionModal,
} from "./ClosePositionModal.types";
import { PerpCloseControls } from "./PerpCloseControls";
import {
  alignCloseLimitPrice,
  closeSizeExceedsPosition,
  resolvePerpCloseSize,
} from "./ClosePositionModal.perpAction";
import { useClosePositionState } from "./useClosePositionState";
import { useClosePositionSubmit } from "./useClosePositionSubmit";
import { useClosePositionRFQ } from "./useClosePositionRFQ";
import { useClosePositionPresentation } from "./useClosePositionPresentation";

/**
 * The modal responsible for closing positions
 */
function ClosePositionModal({
  position,
  onHide,
  show,
  orderbook,
  ...modalProps
}: IClosePositionModal) {
  const [loading, setLoading] = useState(false);
  const [canClosePosition, setCanClosePosition] = useState<boolean>(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [warning, setWarning] = useState<string | undefined>(undefined);
  const [showClosePositionConfirmation, setShowClosePositionConfirmation] =
    useState<boolean>(false);
  const [showPnLCard, setShowPnLCard] = useState<boolean>(false);

  // ── Perp close controls (Hyperliquid-style: Market/Limit + partial size) ──
  const [closeMode, setCloseMode] = useState<"market" | "limit">("market");
  // Contracts to close, as a decimal string. Empty => not yet initialised.
  const [closeSizeInput, setCloseSizeInput] = useState<string>("");
  const [limitPriceInput, setLimitPriceInput] = useState<string>("");

  const { t } = useTranslation("app", {
    keyPrefix: "ConfirmationModal.ClosePositionModal",
  });
  const { t: apiError } = useTranslation("apiErrors");
  const { addToast, addErrorToast } = useToast();
  const { playSound } = useSFX();
  const { mutate: mutateCache } = useSWRConfig();
  const revalidationTimersRef = useRef<number[]>([]);
  const lastRevalidationRef = useRef(0);
  const { closePosition } = useOrder();
  const { closePerpPosition, placePerpOrder, cancelRestingCloseOrders } =
    usePerpOrder();
  const { data: accountData, mutate: mutateAccount } = useGetAccount();
  const { orderbook: wsOrderbook } = useOrderbookWSS(
    orderbook ? undefined : position?.instrument_name
  );
  const {
    data: rfqData,
    createRFQ,
    deleteRFQsByInstrumentId,
    getRFQQuotesByBlockId,
  } = useRFQ("taker");

  const { acceptQuote } = useQuotes(false);

  const { getMarketPrecision } = useContext(MarketInstrumentContext);
  const [availablePages, setAvailablePages] = useState<ClosePositionPageEnum[]>(
    [ClosePositionPageEnum.DETAILS]
  );
  const [pageIndex, setPageIndex] = useState(0);

  const orderbookData = wsOrderbook || orderbook;

  // The price a market close would cross, for the slippage bound's preview.
  // Closing a LONG sells into the BID; closing a SHORT buys from the ASK.
  // No touch on that side means no figure, and the row shows a dash.
  const closeReferencePrice = useMemo(() => {
    const closingLong = position?.side === SideResponse.Buy;
    const levels = closingLong ? orderbookData?.bids : orderbookData?.asks;
    const best = Array.isArray(levels) ? levels[0] : undefined;
    const price = Array.isArray(best) ? best[0] : undefined;
    return price === undefined ? undefined : String(price);
  }, [orderbookData?.asks, orderbookData?.bids, position?.side]);

  // ── Perp close-control derived values ──
  const isPerp =
    position?.instrument_type === InstrumentTypeResponse.Perpetual ||
    Boolean(position?.instrument_name?.endsWith("-PERP"));

  const positionAmount = Number(position?.amount || 0);

  const closeSizeNum = Number(closeSizeInput) || 0;

  const {
    closeEstimate,
    closePrecision,
    revalidateAfterPositionClose,
    page,
    onHideModal,
    onOpenPositionConfirmationModal,
    onClosePositionConfirmationModal,
    onOpenPnLCard,
    onHidePnLCard,
  } = useClosePositionState({
    position,
    orderbookData,
    getMarketPrecision,
    show,
    isPerp,
    setCloseMode,
    setCloseSizeInput,
    setLimitPriceInput,
    mutateCache,
    revalidationTimersRef,
    lastRevalidationRef,
    availablePages,
    pageIndex,
    onHide,
    setShowClosePositionConfirmation,
    setShowPnLCard,
  });

  // What is actually submitted: the size floored to `amount_step` (or the whole
  // position), the limit price aligned to `price_step` on the side that is safe
  // for the close. The fields keep what the trader typed.
  const submitCloseSize = resolvePerpCloseSize(
    closeSizeInput,
    position?.amount,
    closePrecision
  );
  const submitLimitPrice = alignCloseLimitPrice(
    limitPriceInput,
    position?.side,
    closePrecision
  );
  const perpCloseValid =
    submitCloseSize !== null &&
    !closeSizeExceedsPosition(closeSizeInput, position?.amount) &&
    (closeMode !== "limit" || Number(submitLimitPrice) > 0);

  const { onClosePosition, executePerpClose } = useClosePositionSubmit({
    position,
    closePerpPosition,
    closePosition,
    placePerpOrder,
    cancelRestingCloseOrders,
    accountData,
    mutateAccount,
    revalidateAfterPositionClose,
    onHide,
    playSound,
    addToast,
    t,
    apiError,
    closeSizeInput: submitCloseSize ?? "",
    closeMode,
    limitPriceInput: submitLimitPrice,
    setError,
    setLoading,
  });

  const {
    selectedQuote,
    selectedRfqBlock,
    hasRequest,
    hasQuote,
    onPrevPage,
    onCreate,
    onAcceptQuote,
    onNextPage,
  } = useClosePositionRFQ({
    position,
    getRFQQuotesByBlockId,
    deleteRFQsByInstrumentId,
    rfqData,
    createRFQ,
    acceptQuote,
    addErrorToast,
    addToast,
    apiError,
    t,
    page,
    pageIndex,
    setPageIndex,
    setError,
    setWarning,
    setLoading,
    onHideModal,
  });

  const { pageContent, stats, tags, confirmationButtons } =
    useClosePositionPresentation({
      show,
      canClosePosition,
      position,
      setError,
      setWarning,
      setPageIndex,
      setCanClosePosition,
      setAvailablePages,
      hasQuote,
      hasRequest,
      page,
      selectedQuote,
      selectedRfqBlock,
      t,
      orderbookData,
      getMarketPrecision,
      closeEstimate,
      isPerp,
      positionAmount,
      closeSizeInput,
      accountData,
      closeMode,
      executePerpClose,
      perpCloseValid,
      onNextPage,
      onOpenPositionConfirmationModal,
      onOpenPnLCard,
      onPrevPage,
      loading,
      onAcceptQuote,
      onCreate,
    });

  if (!position) {
    return null;
  }

  const negativePnl = currency(position.unrealized_pnl || 0).value < 0;
  const primaryColor = negativePnl ? COLORS.down.one : COLORS.up.one;

  if (!showPnLCard) {
    return (
      <ConfirmationModal
        title={
          page === ClosePositionPageEnum.DETAILS
            ? position.side === SideResponse.Buy
              ? t("close_long_position")
              : t("close_short_position")
            : t("rfq")
        }
        primaryColor={primaryColor}
        style={{
          backgroundColor: BACKGROUND_COLORS.seven,
          width: 320,
        }}
        tag={tags}
        stats={stats}
        confirmationButtons={confirmationButtons}
        confirmationWrapperStyle={{ display: "flex" }}
        isLoading={loading}
        onHide={onHideModal}
        show={show}
        error={error}
        warning={warning}
        extras={
          <>
            {isPerp && page === ClosePositionPageEnum.DETAILS && (
              <PerpCloseControls
                closeMode={closeMode}
                setCloseMode={setCloseMode}
                limitPriceInput={limitPriceInput}
                setLimitPriceInput={setLimitPriceInput}
                closeSizeInput={closeSizeInput}
                setCloseSizeInput={setCloseSizeInput}
                closeSizeNum={closeSizeNum}
                positionAmount={positionAmount}
                closePrecision={closePrecision}
                position={position}
                closeReferencePrice={closeReferencePrice}
                t={t}
              />
            )}
            {pageContent}
            <ClosePositionOverlayInfo
              show={showClosePositionConfirmation}
              onClose={onClosePositionConfirmationModal}
              loading={loading}
              error={error}
              onClosePosition={onClosePosition}
              position={position}
              orderbookData={orderbookData}
            />
          </>
        }
        {...modalProps}
      />
    );
  }

  return (
    <PnLCardModal
      pnlType="position"
      pnlData={position}
      onHideModal={onHideModal}
      show={!!position}
      onHidePnLCard={onHidePnLCard}
    />
  );
}

export default ClosePositionModal;
